import type {
  AuthorizationProvider,
  CredentialAvailability,
  ManagedCredentialOption,
  ProfileActivityType,
  ProfileCheck,
  ProfileCheckState,
  ProfileLifecycle,
  ProfilePurpose,
  ProfileReasonCode,
  ProfileRegistration,
} from "@/api/temp/OAuthClientProfileService";

/**
 * Synthetic OAuth client profiles for the mock tenant. TrustLogix-hosted
 * documents live on `trustlogix.io`; customer and partner documents on third-party
 * `.com` hosts. None of these URLs is fetched - the mock validator derives
 * each result from the URL itself.
 *
 * Pure data: `mcpConnectivity.ts` imports the ids and URLs so saved service
 * configurations and the gateway reference the same records the admin page
 * lists. Usage counts are never written here - they are derived from those
 * configurations.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const GATEWAY_METADATA_URL =
  "https://mcp-gateway.trustlogix.io/.well-known/oauth-client/production-mcp-gateway";
export const GATEWAY_CLIENT_ID = "tlx-production-mcp-gateway";

export const DISCOVERY_CLIENT = {
  id: "cimd-discovery-client",
  name: "TrustLogix discovery client",
  metadataUrl: "https://clients.trustlogix.io/oauth/discovery-client.json",
} as const;

export const RUNTIME_CLIENT = {
  id: "cimd-runtime-client",
  name: "TrustLogix runtime client",
  metadataUrl: "https://clients.trustlogix.io/oauth/runtime-client.json",
} as const;

export const RETIRED_DISCOVERY_CLIENT = {
  id: "ocp-retired-discovery",
  name: "Retired discovery client",
  metadataUrl:
    "https://clients.trustlogix.io/oauth/retired-discovery-client.json",
} as const;

/** The tenant gateway's client profile, per issuer it is registered with. */
export const GATEWAY_PROFILE_IDS = {
  CIMD: "ocp-production-gateway",
  CODE_HOST: "ocp-gateway-code-host",
  CRM: "ocp-gateway-crm",
} as const;

export const TENANT_ENVIRONMENTS = [
  { id: "production", name: "Production" },
  { id: "staging", name: "Staging" },
  { id: "development", name: "Development" },
];

/**
 * Demo authorization-provider integrations - in mock mode, treated as
 * configured under Administration → Integrations, so each carries its saved
 * issuer and endpoints. Tenant IDs and domains are synthetic; `demo: true`
 * keeps the "no provider connection has been verified" notice, and nothing
 * here is ever requested.
 */
export const DEMO_AUTHORIZATION_PROVIDERS: AuthorizationProvider[] = [
  {
    id: "prov-entra-workforce",
    name: "Microsoft Entra ID",
    context: "Workforce tenant · Production",
    issuer: "https://login.microsoftonline.com/4f1c2a7e-8b3d-4c6a-9e21-5d7f0b8a3c14/v2.0",
    issuerSource: "SAVED_CONFIGURATION",
    verification: "NOT_CHECKED",
    endpoints: {
      metadataUrl:
        "https://login.microsoftonline.com/4f1c2a7e-8b3d-4c6a-9e21-5d7f0b8a3c14/v2.0/.well-known/openid-configuration",
      authorizationEndpoint:
        "https://login.microsoftonline.com/4f1c2a7e-8b3d-4c6a-9e21-5d7f0b8a3c14/oauth2/v2.0/authorize",
      tokenEndpoint:
        "https://login.microsoftonline.com/4f1c2a7e-8b3d-4c6a-9e21-5d7f0b8a3c14/oauth2/v2.0/token",
    },
    demo: true,
  },
  {
    id: "prov-entra-partner",
    name: "Microsoft Entra ID",
    context: "Partner tenant · Staging",
    issuer: "https://login.microsoftonline.com/9a6e3b21-0c4f-4d8e-b7a2-1f5c9d3e6a80/v2.0",
    issuerSource: "SAVED_CONFIGURATION",
    verification: "NOT_CHECKED",
    endpoints: {
      metadataUrl:
        "https://login.microsoftonline.com/9a6e3b21-0c4f-4d8e-b7a2-1f5c9d3e6a80/v2.0/.well-known/openid-configuration",
      authorizationEndpoint:
        "https://login.microsoftonline.com/9a6e3b21-0c4f-4d8e-b7a2-1f5c9d3e6a80/oauth2/v2.0/authorize",
      tokenEndpoint:
        "https://login.microsoftonline.com/9a6e3b21-0c4f-4d8e-b7a2-1f5c9d3e6a80/oauth2/v2.0/token",
    },
    demo: true,
  },
  {
    id: "prov-okta-custom",
    name: "Okta",
    context: "Custom authorization server · Production",
    issuer: "https://gigbank.okta.com/oauth2/aus8k2mcpgateway01",
    issuerSource: "SAVED_CONFIGURATION",
    verification: "NOT_CHECKED",
    endpoints: {
      metadataUrl:
        "https://gigbank.okta.com/oauth2/aus8k2mcpgateway01/.well-known/oauth-authorization-server",
      authorizationEndpoint:
        "https://gigbank.okta.com/oauth2/aus8k2mcpgateway01/v1/authorize",
      tokenEndpoint:
        "https://gigbank.okta.com/oauth2/aus8k2mcpgateway01/v1/token",
    },
    demo: true,
  },
];

/** References into the managed credential system - labels, never material. */
export const MANAGED_CREDENTIALS: ManagedCredentialOption[] = [
  {
    id: "cred-prod-signing",
    label: "Production client signing key",
    method: "private_key_jwt",
    availability: "USABLE",
    environmentId: "production",
  },
  {
    id: "cred-discovery-signing",
    label: "Discovery client signing key",
    method: "private_key_jwt",
    availability: "USABLE",
    environmentId: "production",
  },
  {
    id: "cred-dev-signing",
    label: "Development signing key",
    method: "private_key_jwt",
    availability: "USABLE",
    environmentId: "development",
  },
  {
    id: "cred-finance-restricted",
    label: "Finance signing key",
    method: "private_key_jwt",
    availability: "RESTRICTED",
  },
  {
    id: "cred-gateway-secret",
    label: "Gateway client credential",
    method: "client_secret_basic",
    availability: "USABLE",
    environmentId: "production",
  },
  {
    id: "cred-legacy-integration",
    label: "Legacy integration credential",
    method: "client_secret_basic",
    availability: "USABLE",
    environmentId: "production",
  },
];

export interface ProfileSeed {
  id: string;
  name: string;
  description?: string;
  environmentIds: string[];
  purposes: ProfilePurpose[];
  lifecycle: ProfileLifecycle;
  identityLocked: boolean;
  registration: ProfileRegistration;
  managedCredentialRef?: string;
  /** Credential availability when no reference is set. */
  credentialAvailability?: CredentialAvailability;
  /** How long ago the recorded check ran; absent: never checked. */
  checkedAgoMs?: number;
  checkState?: ProfileCheckState;
  checks?: ProfileCheck[];
  /** The published document has changed since the recorded check. */
  documentChangedSinceCheck?: boolean;
  createdAgoMs: number;
  updatedAgoMs: number;
  activity: Array<{ type: ProfileActivityType; agoMs: number }>;
}

const passed = (
  ...codes: ProfileCheck["code"][]
): ProfileCheck[] => codes.map(code => ({ code, state: "PASSED" }));

const CIMD_CHECKS = passed(
  "METADATA_RETRIEVAL",
  "DOCUMENT_VALIDATION",
  "CLIENT_ID_MATCH",
  "MANAGED_AUTHENTICATION",
);

const CLIENT_CHECKS = passed(
  "DETAILS_COMPLETE",
  "ISSUER_BINDING",
  "MANAGED_AUTHENTICATION",
);

const cimd = (clientIdUrl?: string): ProfileRegistration => ({
  kind: "CIMD",
  metadataSource: "EXISTING_PUBLISHED_DOCUMENT",
  clientIdUrl,
});

const failed = (
  code: ProfileCheck["code"],
  reasonCode: ProfileReasonCode,
): ProfileCheck => ({ code, state: "FAILED", reasonCode });

const lifecycleActivity = (
  createdAgoMs: number,
  checkedAgoMs: number,
): ProfileSeed["activity"] => [
  { type: "CREATED", agoMs: createdAgoMs },
  { type: "VALIDATED", agoMs: checkedAgoMs },
  { type: "MADE_AVAILABLE", agoMs: checkedAgoMs },
];

export const PROFILE_SEEDS: ProfileSeed[] = [
  {
    id: GATEWAY_PROFILE_IDS.CIMD,
    name: "Production Gateway Client",
    description:
      "The MCP Access Gateway's client identity with issuers that accept client metadata documents.",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: cimd(GATEWAY_METADATA_URL),
    managedCredentialRef: "cred-prod-signing",
    checkedAgoMs: 5 * MINUTE,
    checkState: "PASSED",
    checks: CIMD_CHECKS,
    createdAgoMs: 60 * DAY,
    updatedAgoMs: 5 * MINUTE,
    activity: lifecycleActivity(60 * DAY, 5 * MINUTE),
  },
  {
    id: GATEWAY_PROFILE_IDS.CODE_HOST,
    name: "Gateway Client · Code Hosting",
    description:
      "The gateway's pre-registered client at the code-hosting authorization server.",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: {
      kind: "EXISTING_OAUTH_CLIENT",
      clientId: GATEWAY_CLIENT_ID,
      issuer: "https://auth.code.contoso.com",
    },
    managedCredentialRef: "cred-gateway-secret",
    checkedAgoMs: 3 * DAY,
    checkState: "PASSED",
    checks: CLIENT_CHECKS,
    createdAgoMs: 90 * DAY,
    updatedAgoMs: 3 * DAY,
    activity: lifecycleActivity(90 * DAY, 3 * DAY),
  },
  {
    id: GATEWAY_PROFILE_IDS.CRM,
    name: "Gateway Client · CRM",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: {
      kind: "EXISTING_OAUTH_CLIENT",
      clientId: GATEWAY_CLIENT_ID,
      issuer: "https://login.crm.contoso.com",
    },
    managedCredentialRef: "cred-gateway-secret",
    checkedAgoMs: 3 * DAY,
    checkState: "PASSED",
    checks: CLIENT_CHECKS,
    createdAgoMs: 90 * DAY,
    updatedAgoMs: 3 * DAY,
    activity: lifecycleActivity(90 * DAY, 3 * DAY),
  },
  {
    id: DISCOVERY_CLIENT.id,
    name: DISCOVERY_CLIENT.name,
    description: "Reads MCP service metadata during discovery.",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: cimd(DISCOVERY_CLIENT.metadataUrl),
    managedCredentialRef: "cred-discovery-signing",
    checkedAgoMs: 9 * DAY,
    checkState: "PASSED",
    checks: CIMD_CHECKS,
    // Its publisher changed the document after the last check.
    documentChangedSinceCheck: true,
    createdAgoMs: 45 * DAY,
    updatedAgoMs: 9 * DAY,
    activity: [
      ...lifecycleActivity(45 * DAY, 9 * DAY),
      { type: "DOCUMENT_CHANGED", agoMs: 2 * DAY },
    ],
  },
  {
    id: RUNTIME_CLIENT.id,
    name: RUNTIME_CLIENT.name,
    description: "Calls MCP tools at runtime with client credentials.",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: cimd(RUNTIME_CLIENT.metadataUrl),
    managedCredentialRef: "cred-prod-signing",
    checkedAgoMs: 2 * HOUR,
    checkState: "PASSED",
    checks: CIMD_CHECKS,
    createdAgoMs: 45 * DAY,
    updatedAgoMs: 2 * HOUR,
    activity: lifecycleActivity(45 * DAY, 2 * HOUR),
  },
  {
    id: "ocp-legacy-integration",
    name: "Legacy Integration Client",
    description: "A client pre-registered with the warehouse authorization server.",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: {
      kind: "EXISTING_OAUTH_CLIENT",
      clientId: "svc-legacy-integration",
      issuer: "https://warehouse.contoso.com/oauth",
    },
    managedCredentialRef: "cred-legacy-integration",
    checkedAgoMs: 6 * HOUR,
    checkState: "PASSED",
    checks: CLIENT_CHECKS,
    createdAgoMs: 120 * DAY,
    updatedAgoMs: 6 * HOUR,
    activity: lifecycleActivity(120 * DAY, 6 * HOUR),
  },
  {
    id: "ocp-inventory-scanner",
    name: "Inventory Scanner Client",
    description: "For the development discovery scanner. Metadata URL pending.",
    environmentIds: ["development"],
    purposes: ["METADATA_DISCOVERY"],
    lifecycle: "DRAFT",
    identityLocked: false,
    registration: cimd(),
    createdAgoMs: 1 * DAY,
    updatedAgoMs: 1 * DAY,
    activity: [{ type: "CREATED", agoMs: 1 * DAY }],
  },
  {
    id: "ocp-partner-sandbox",
    name: "Partner Sandbox Client",
    environmentIds: ["development"],
    purposes: ["RUNTIME_ACCESS"],
    lifecycle: "DRAFT",
    identityLocked: false,
    registration: cimd("https://partner-sandbox.com/oauth/mismatch-client.json"),
    managedCredentialRef: "cred-dev-signing",
    checkedAgoMs: 3 * HOUR,
    checkState: "FAILED",
    checks: [
      { code: "METADATA_RETRIEVAL", state: "PASSED" },
      { code: "DOCUMENT_VALIDATION", state: "PASSED" },
      failed("CLIENT_ID_MATCH", "CLIENT_ID_MISMATCH"),
      { code: "MANAGED_AUTHENTICATION", state: "PASSED" },
    ],
    createdAgoMs: 4 * HOUR,
    updatedAgoMs: 3 * HOUR,
    activity: [
      { type: "CREATED", agoMs: 4 * HOUR },
      { type: "VALIDATED", agoMs: 3 * HOUR },
    ],
  },
  {
    id: "ocp-analytics-runtime",
    name: "Analytics Runtime Client",
    environmentIds: ["staging"],
    purposes: ["RUNTIME_ACCESS"],
    lifecycle: "DRAFT",
    identityLocked: false,
    registration: cimd("https://analytics.contoso.com/oauth/runtime-client.json"),
    credentialAvailability: "MISSING",
    createdAgoMs: 2 * HOUR,
    updatedAgoMs: 2 * HOUR,
    activity: [{ type: "CREATED", agoMs: 2 * HOUR }],
  },
  {
    id: RETIRED_DISCOVERY_CLIENT.id,
    name: RETIRED_DISCOVERY_CLIENT.name,
    description: "Replaced by the TrustLogix discovery client.",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY"],
    lifecycle: "ARCHIVED",
    identityLocked: true,
    registration: cimd(RETIRED_DISCOVERY_CLIENT.metadataUrl),
    managedCredentialRef: "cred-discovery-signing",
    checkedAgoMs: 40 * DAY,
    checkState: "PASSED",
    checks: CIMD_CHECKS,
    createdAgoMs: 200 * DAY,
    updatedAgoMs: 30 * DAY,
    activity: [
      ...lifecycleActivity(200 * DAY, 40 * DAY),
      { type: "ARCHIVED", agoMs: 30 * DAY },
    ],
  },
  {
    // Preserved through the adapter: managed on the service that created it.
    id: "ocp-legacy-dcr-risk-models",
    name: "Dynamically registered client · Markets Risk",
    environmentIds: ["production"],
    purposes: ["METADATA_DISCOVERY", "RUNTIME_ACCESS"],
    lifecycle: "AVAILABLE",
    identityLocked: true,
    registration: {
      kind: "LEGACY_READ_ONLY",
      originalMethod: "DCR",
      clientId: "dcr-7f3a9c2e",
      issuer: "https://login.contoso.com/v2.0",
      sourceRef: "mcp-databricks-risk-models",
      sourceName: "Markets Risk",
    },
    credentialAvailability: "NOT_CHECKED",
    checkState: "NOT_APPLICABLE",
    createdAgoMs: 150 * DAY,
    updatedAgoMs: 150 * DAY,
    activity: [{ type: "CREATED", agoMs: 150 * DAY }],
  },
];
